import { AlertCircle, Trash2 } from "lucide-react";
import { useState } from "react";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { useWalletArrangement } from "../hooks/useWalletArrangement";
import type { RuntimeWallet } from "../types/payments-wallets.types";
import { WalletTargetSummary } from "./WalletTargetSummary";

export interface DeleteWalletDialogProps {
  open: boolean;
  onClose: () => void;
  /** A wallet the card judged never used (`isWalletDeletable`); the backend still has the last word. */
  wallet: RuntimeWallet;
  /** Called after the backend deleted it, so the page can drop the card without reloading the grid. */
  onDeleted: (walletId: string) => void;
}

/**
 * Confirms deleting a wallet the organization activated and never used. The point of the dialog is to say
 * that nothing is lost: the wallet holds no money and no history, and activating its currency again — or
 * money arriving in it — brings the same wallet back.
 *
 * A refusal (409 `wallet_in_use`: a collection or payout in that currency is still open, or it moved since
 * the card was drawn) stays in the dialog with the backend's reason and disables the confirm button, because
 * asking again would get the same answer.
 */
export function DeleteWalletDialog({ open, onClose, wallet, onDeleted }: DeleteWalletDialogProps) {
  const { t } = useTranslations("wallets");
  const { deleteWallet, isDeleting } = useWalletArrangement();
  const [refusal, setRefusal] = useState<string | null>(null);

  const close = () => {
    setRefusal(null);
    onClose();
  };

  const handleConfirm = async () => {
    try {
      await deleteWallet(wallet.id);
      colocaToast.success(t("delete_success"));
      onDeleted(wallet.id);
      close();
    } catch (err: unknown) {
      const message = HttpApiService.getErrorMessage(err) || t("delete_error");
      if (HttpApiService.isAxiosError(err) && err.response?.status === 409) {
        setRefusal(message);
      } else {
        colocaToast.error(message);
      }
    }
  };

  const points = [
    t("delete_point_list"),
    t("delete_point_reactivate"),
    t("delete_point_incoming", { currency: wallet.currency }),
  ];

  return (
    <ColocaAlertDialog
      open={open}
      // Confirming never dismisses on its own: only a success closes, and Escape / outside-click cannot
      // detach the dialog from a request in flight.
      onOpenChange={(next) => {
        if (!next && !isDeleting) close();
      }}
      media={<Trash2 className="size-8 text-destructive" />}
      title={t("delete_title", { currency: wallet.currency })}
      description={t("delete_desc")}
      confirmLabel={t("delete_confirm")}
      confirmVariant="destructive"
      cancelLabel={t("delete_cancel")}
      confirmDisabled={isDeleting || refusal !== null}
      onConfirm={() => void handleConfirm()}
    >
      <div className="space-y-3">
        <WalletTargetSummary wallet={wallet} />
        {refusal ? (
          <ColocaAlert variant="destructive" icon={AlertCircle} text={refusal} />
        ) : (
          <ul className="space-y-1.5 text-left text-sm text-muted-foreground">
            {points.map((point) => (
              <li key={point} className="flex gap-2">
                <span
                  className="mt-2 h-1 w-1 shrink-0 rounded-full bg-muted-foreground/60"
                  aria-hidden="true"
                />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </ColocaAlertDialog>
  );
}
